import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ArrowRight } from '@phosphor-icons/react';
import LustLinkSeal from '../components/common/LustLinkSeal';

const IMG_LEFT = 'https://images.unsplash.com/photo-1559748177-4199f9175c12?w=900&q=80&auto=format';
const IMG_RIGHT = 'https://images.unsplash.com/photo-1754475205146-23ca0cd6e73f?w=900&q=80&auto=format';

const SplashPage = () => {
  return (
    <div className="min-h-screen bg-black relative overflow-hidden" data-testid="splash-page">

      {/* Left image */}
      <div className="hidden lg:block fixed left-0 top-0 w-[28%] h-full z-0">
        <img src={IMG_LEFT} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />
      </div>

      {/* Right image */}
      <div className="hidden lg:block fixed right-0 top-0 w-[28%] h-full z-0">
        <img src={IMG_RIGHT} alt="" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />
      </div>

      {/* Mobile background */}
      <div className="lg:hidden absolute inset-0 grid grid-cols-2 gap-0 opacity-20">
        <img src={IMG_LEFT} alt="" className="w-full h-full object-cover" />
        <img src={IMG_RIGHT} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />

      {/* Center content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <LustLinkSeal size={56} />
          <span className="font-heading text-3xl font-black tracking-tight text-white">
            Lust<span className="text-blue-500">Link</span>
          </span>
        </motion.div>

        {/* Created by a creator */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-slate-400 italic text-sm mb-8"
        >
          A platform for content creators, built by a content creator.
        </motion.p>

        {/* Main headline */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mb-5"
        >
          <h1 className="font-heading text-[7rem] sm:text-[9rem] md:text-[11rem] font-black tracking-tighter text-white leading-[0.85] mb-0">
            90<span className="text-blue-500">%</span>
          </h1>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-none">
            <span className="text-white">MINIMUM. </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-300 to-white">ALWAYS.</span>
          </h2>
        </motion.div>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-slate-300 text-lg sm:text-xl max-w-lg mb-3 leading-relaxed"
        >
          Every creator keeps <span className="text-white font-bold">at least 90%</span> of every dollar.
          Refer other creators and earn up to <span className="text-white font-bold">95%</span>.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-blue-400/90 font-semibold text-base mb-3"
        >
          +1% per active referral. Up to 5 referrals. No cap on time.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-slate-500 text-sm max-w-md mb-10"
        >
          No hidden fees. No time limits. The best split in the industry — because we actually know what it's like to create.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="flex flex-col sm:flex-row items-center gap-4 mb-12"
        >
          <Link
            to="/auth?mode=register&role=creator"
            className="group px-10 py-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full font-bold text-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-2xl shadow-blue-500/30 flex items-center gap-3"
            data-testid="splash-signup-btn"
          >
            Start Creating
            <ArrowRight weight="bold" className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/home"
            className="px-8 py-4 border border-white/20 text-slate-300 rounded-full font-medium text-lg hover:bg-white/5 hover:text-white transition-colors"
            data-testid="splash-enter-btn"
          >
            Enter Site
          </Link>
        </motion.div>

        {/* Comparison */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="flex items-center gap-8 mb-10"
        >
          <div className="text-center">
            <p className="text-3xl font-black text-white">90-95<span className="text-blue-500">%</span></p>
            <p className="text-xs text-blue-400 mt-1 font-semibold">LustLink</p>
          </div>
          <div className="text-slate-700 text-sm font-bold">vs</div>
          <div className="text-center opacity-40">
            <p className="text-3xl font-black text-slate-500">80%</p>
            <p className="text-xs text-slate-600 mt-1">OnlyFans</p>
          </div>
          <div className="text-slate-700 text-sm font-bold">vs</div>
          <div className="text-center opacity-40">
            <p className="text-3xl font-black text-slate-500">70%</p>
            <p className="text-xs text-slate-600 mt-1">Fansly</p>
          </div>
        </motion.div>

        {/* Referral bonus breakdown */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-6 py-4 max-w-sm mb-10"
        >
          <p className="text-white font-semibold text-sm mb-2">Referral Bonus Structure:</p>
          <div className="grid grid-cols-5 gap-2 text-center">
            {[1,2,3,4,5].map(n => (
              <div key={n}>
                <p className="text-blue-400 font-bold text-lg">{90+n}%</p>
                <p className="text-[10px] text-slate-500">{n} ref{n > 1 ? 's' : ''}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Fine print */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="max-w-lg text-[10px] text-slate-600 leading-relaxed"
        >
          <p>
            All creators receive a minimum 90% revenue share. +1% per active referral per month (max 5 referrals = 95%).
            A referral becomes "active" once the referred creator has produced 10+ hours of live content or 25 unique photos/videos for sale (bundled or individual).
            The 90% minimum is non-negotiable and applies for the lifetime of the account. Accounts previously locked at 95% will be honored.
            Accounts are non-transferable. New account disputes reviewed case-by-case.
            All creators must comply with our Terms of Service and Content Policy. Must be 18+ to register.
          </p>
          <p className="mt-2">&copy; 2026 Ganso Gordo Producciones LLC. All rights reserved.</p>
        </motion.div>
      </div>
    </div>
  );
};

export default SplashPage;
